import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs } from '@otp/ui';
import clsx from 'clsx';
import {
  WttpQuestionnaireResponseListFilterInput,
  Actions,
  Subjects,
  AppArea,
  type User,
} from 'src/graphql/generated/graphql';
import { WttpQuestionnaireResponsesTable } from './table/table';
import { WttpQuestionnaireCardList } from './card/list';
import { useTable } from 'src/hooks/use-table';
import { Filter } from './filter';
import { useAuthContext } from 'src/context/auth.context';
import { useDeviceContext } from 'src/context/device.context';
import { WttpQuestionnaireResponseModal } from '../questionnaire-response-modal';
import { FilterWrapper } from 'src/components/common/wrapper/filter/wrapper';
import { ContentWrapper } from 'src/components/common/wrapper/content/wrapper';
import { TableWrapper } from 'src/components/common/wrapper/table/wrapper';
import { Can } from 'src/utils/can';
import { useQuestionnaireResponses } from 'src/hooks/query/use-questionnaire-responses';
import { getWttpResponseFiltersBasedOnUser } from './utils';

const PAGINATION_LIMIT_DESKTOP = 10;
const PAGINATION_LIMIT_MOBILE = 5;

enum TabType {
  Active = 'active',
  History = 'history',
}

export const WttpQuestionnaireResponseList = () => {
  const { t } = useTranslation();
  const { isMobile } = useDeviceContext();
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const { isAdmin, isOtpPro, user } = useAuthContext();
  const paginationLimit = isMobile
    ? PAGINATION_LIMIT_MOBILE
    : PAGINATION_LIMIT_DESKTOP;

  const TABS = [
    { label: t('common.active'), value: TabType.Active },
    { label: t('common.history'), value: TabType.History },
  ];

  const [activeTab, setActiveTab] = useState(TabType.Active);

  const { page, setPage, filter, onFilterChange, defaultFilter } = useTable<
    WttpQuestionnaireResponseListFilterInput,
    Record<any, any>
  >({
    defaultFilter: {
      ...getWttpResponseFiltersBasedOnUser(user as User, { isAdmin, isOtpPro }),
    },
  });

  const { loading, totalCount, wttpQuestionnaireResponses, refetch } =
    useQuestionnaireResponses({
      filter: {
        ...filter,
        isActive: activeTab === TabType.Active,
        storeIds: filter.storeIds?.length ? filter.storeIds : undefined,
      },
      pagination: {
        limit: paginationLimit,
        offset: page * paginationLimit,
      },
    });

  const onTabChange = (_: any, value: any) => {
    setActiveTab(value);
    setPage(0);
  };

  const onRowClick = (_: any, { rowIndex }: { rowIndex: number }) => {
    const wttpQuestionnaireResponse = wttpQuestionnaireResponses[rowIndex];

    setSelectedRowId(wttpQuestionnaireResponse.id);
  };

  return (
    <Can
      I={Actions.Read}
      a={Subjects.QuestionnaireResponses}
      appArea={AppArea.Wttp}
    >
      <FilterWrapper>
        <Filter onSubmit={onFilterChange} defaultFilter={defaultFilter} />
      </FilterWrapper>

      <ContentWrapper>
        <TableWrapper>
          <div className={clsx('relative', { 'flex justify-end': isAdmin })}>
            {!isAdmin && (
              <Tabs
                tabs={TABS}
                onChange={onTabChange}
                value={activeTab}
                variant={isMobile ? 'fullWidth' : undefined}
              />
            )}
          </div>
          {!isMobile ? (
            <WttpQuestionnaireResponsesTable
              data={wttpQuestionnaireResponses}
              className="mt-4"
              loading={loading}
              options={{
                page,
                count: totalCount,
                sort: true,
                rowsPerPage: paginationLimit,
                onChangePage: setPage,
                enableNestedDataAccess: '.',
                onRowClick,
              }}
              // onActionFinished={onActionFinished}
            />
          ) : (
            <WttpQuestionnaireCardList
              data={wttpQuestionnaireResponses}
              page={page}
              rowsPerPage={paginationLimit}
              onPageChange={setPage}
              totalCount={totalCount}
            />
          )}
        </TableWrapper>
      </ContentWrapper>
      {selectedRowId && (
        <WttpQuestionnaireResponseModal
          questionnaireResponseId={selectedRowId}
          onClose={() => {
            setSelectedRowId(null);
            refetch();
          }}
        />
      )}
    </Can>
  );
};
